import moment from "moment-timezone";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import NotShowContentInMobileSize from "../../NotShowContentInMobileSize";
import TimeRangeSlider from "./timeRangeSlider";

export type weekDays =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

export type DayRangesType = {
  sunday: number[];
  monday: number[];
  tuesday: number[];
  wednesday: number[];
  thursday: number[];
  friday: number[];
  saturday: number[];
};

export const convertH2M = (timeInHour: string) => {
  const timeParts = timeInHour.split(":");
  return Number(timeParts[0]) * 60 + Number(timeParts[1]);
};

export const splitArrayIntoChunksOfLen = (arr: Array<any>, len: any) => {
  const chunks: any[] = [];
  let i = 0;
  const n = arr.length;
  while (i < n) {
    chunks.push(arr.slice(i, (i += len)));
  }
  return chunks;
};

export const getTimeZoneOffset = (date: Date, timeZone: string): number => {
  // Check if the timeZone is a valid string
  if (typeof timeZone !== "string") {
    throw new Error("Invalid time zone provided");
  }

  // Create a new DateTimeFormat object with the provided time zone
  const options: Intl.DateTimeFormatOptions = { timeZone, hour12: false };
  const formatter = new Intl.DateTimeFormat("en-US", options);

  // Format the date using the provided time zone
  const localTime = new Date(date);
  const targetTime = new Date(date.toLocaleString("en-US", { timeZone }));

  // Return the difference in minutes between the target time zone and local time
  return (targetTime.getTime() - localTime.getTime()) / (1000 * 60);
};

export const getTimeFromMins = (mins: number, day: weekDays) => {
  const h = (mins / 60) | 0,
    m = mins % 60 | 0;
  return moment.utc().day(day).hours(h).minutes(m);
};

export const dayMapper = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
} as {
  sunday: number;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
};

type Props = {
  times: DayRangesType;
  handleTimes: (times: DayRangesType) => void;
};

const OperatingHours: FC<Props> = ({ times, handleTimes }) => {
  const { t } = useTranslation();

  const checkValue = (a: number, b: number, day: weekDays) => {
    let f = false;
    const twoPairs = splitArrayIntoChunksOfLen(times[day], 2);
    twoPairs.forEach((v, i) => {
      if (a === v[0] && b === v[1]) {
        f = true;
      }
    });
    return f;
  };

  const removeSlide = (removeEl: any, day: weekDays) => {
    const val = removeEl.value;
    const twoPairs = splitArrayIntoChunksOfLen(times[day], 2);

    const newArr = twoPairs.filter((v) => !v.includes(val)).flat();

    handleTimes({ ...times, [day]: newArr });
  };

  const addSlide = (addEl: any, day: weekDays) => {
    const val = addEl.value;

    let nextLeft = 0;
    let nextRight = 0;
    const twoPairs = splitArrayIntoChunksOfLen(times[day], 2);

    twoPairs.forEach((v) => {
      if (v[0] === val) {
        nextRight = val - 60;
        nextLeft = val - 120;
      } else if (v[1] === val) {
        nextLeft = val + 60;
        nextRight = val + 120;
      }
    });
    const newArr = twoPairs
      .filter((v) => v.includes(nextRight) || v.includes(nextLeft))
      .flat();

    if (
      newArr.length === 0 &&
      nextLeft > 0 &&
      nextRight > 0 &&
      nextLeft <= 1380 &&
      nextRight <= 1439
    ) {
      const a = [...times[day], nextLeft, nextRight].sort((a, b) => a - b);

      handleTimes({ ...times, [day]: a });
    }
  };

  const updateHandler = (e: readonly number[], day: weekDays) => {
    handleTimes({ ...times, [day]: [...e] });
  };

  const changeHandler = (e: readonly number[], day: weekDays) => {
    handleTimes({ ...times, [day]: [...e] });
  };

  const addNewToEmpty = (day: weekDays) => {
    if (times[day].length === 0) {
      handleTimes({ ...times, [day]: [480, 1200] });
    }
  };

  return (
    <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex tw-py-6">
      <div>
        <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
          {t("VisibilityManagerEdit/key3")}
        </div>
        <div className="tw-self-stretch tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
          {t("VisibilityManagerEdit/key4")}
        </div>
      </div>

      <div className="md:tw-block tw-hidden tw-w-full">
        {[
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday"
        ].map((day, index) => (
          <TimeRangeSlider
            key={day + index.toString()}
            day={day as weekDays}
            data={times[day as weekDays]}
            addSlide={addSlide}
            removeSlide={removeSlide}
            updateHandler={updateHandler}
            changeHandler={changeHandler}
            checkValue={checkValue}
            addNewToEmpty={addNewToEmpty}
          />
        ))}
      </div>

      <div className="md:tw-hidden tw-block">
        <NotShowContentInMobileSize
          title=""
          description={t("VisibilityManagerEdit/key6")}
        />
      </div>
    </div>
  );
};

export default OperatingHours;
